import { deserializeDate } from "@util";
import { Properties } from "@util/types";
import PricingRecord, {
	PricingSettings,
	pricingSettingsSchema,
} from "./PricingRecord";
import { DivisionStatus } from "./DivisionStatus";
import z from "zod";
import { providerSettingsSchema } from "./AssetDivision";

export const newDivisionSchema = z.object({
	name: z.string(),
	isMainDivision: z.boolean().nullable(),
	street: z.string().nullable(),
	zip: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	fax: z.string().nullable(),
	phone: z.string().nullable(),
	stid: z.string().nullable(),
	ustid: z.string().nullable(),
	status: z.enum(Object.values(DivisionStatus)),
	providerSettings: providerSettingsSchema,
	comment: z.string().nullable(),
	isSachbezugFitnessEnabled: z.boolean(),
});

export default class Division {
	constructor(props: Properties<Division>) {
		this.divisionId = props.divisionId;
		this.customerId = props.customerId;
		this.isMainDivision = props.isMainDivision;
		this.name = props.name;
		this.status = props.status;
		this.street = props.street;
		this.zip = props.zip;
		this.city = props.city;
		this.country = props.country;
		this.phone = props.phone;
		this.fax = props.fax;
		this.created = props.created;
		this.updated = props.updated;
		this.stid = props.stid;
		this.ustid = props.ustid;
		this.reportType = props.reportType;
		this.financialSoftwareId = props.financialSoftwareId;
		this.excessBudgetSettings = props.excessBudgetSettings;
		this.pricingSettings = props.pricingSettings;
		this.autoLockDayOfMonth = props.autoLockDayOfMonth;
		this.comment = props.comment;
		this.reviewSettings = props.reviewSettings;
		this.hasWebAppAccess = props.hasWebAppAccess;
		this.allowThirdPartyVouchers = props.allowThirdPartyVouchers;
		this.providerSettings = props.providerSettings;
		this.isSachbezugFitnessEnabled = props.isSachbezugFitnessEnabled;
		this.categories = props.categories;
	}

	readonly divisionId: string;
	readonly customerId: string;
	readonly name: string;
	readonly status: DivisionStatus;
	readonly isMainDivision: boolean | null;
	readonly street: string | null;
	readonly zip: string | null;
	readonly city: string | null;
	readonly country: string | null;
	readonly phone: string | null;
	readonly fax: string | null;
	readonly created: Date | null;
	readonly updated: Date | null;
	readonly stid: string | null;
	readonly ustid: string | null;
	readonly reportType: string | null;
	readonly financialSoftwareId: string | null;
	readonly excessBudgetSettings: ExcessBudgetSettings;
	readonly pricingSettings: PricingSettings | null;
	readonly autoLockDayOfMonth: number | null;
	readonly comment: string | null;
	readonly reviewSettings: ReviewSettings | null;
	readonly hasWebAppAccess: boolean;
	readonly allowThirdPartyVouchers: boolean;
	readonly providerSettings: {
		readonly jobrad?: {
			readonly frameContractUUID: string;
			readonly companyId: string;
			readonly companyUrl: string;
		};
	};
	readonly isSachbezugFitnessEnabled: boolean;
	readonly categories: string[];

	private static schema = newDivisionSchema.extend({
		divisionId: z.string(),
		customerId: z.string(),
		created: z.coerce.date().nullable(),
		updated: z.coerce.date().nullable(),
		reportType: z.string().nullable(),
		financialSoftwareId: z.string().nullable(),
		excessBudgetSettings: z.object({
			savingPeriodStartMonth: z.number().min(1).max(12).nullable(),
			expirationTransitionTimeInMonths: z.number().min(1).max(6).nullable(),
		}),
		autoLockDayOfMonth: z.number().nullable(),
		pricingSettings: pricingSettingsSchema.nullable(),
		reviewSettings: z
			.object({
				shouldReviewTrips: z.boolean(),
				minAmountToReviewTrips: z.number().nullable(),
				maxTripAgeInDaysForAutoApproval: z.number().nullable(),
			})
			.nullable(),
		hasWebAppAccess: z.boolean(),
		allowThirdPartyVouchers: z.boolean(),
		categories: z.array(z.string()),
	});

	public static fromJson(data: any): Division {
		const validated = Division.schema.parse(data);
		return new Division({
			...validated,
			pricingSettings: validated.pricingSettings
				? validated.pricingSettings.map(PricingRecord.fromJson)
				: null,
			created: deserializeDate.nullable.dateTime(data["created"]),
			updated: deserializeDate.nullable.dateTime(data["updated"]),
		});
	}
}

type ExcessBudgetSettings = {
	readonly savingPeriodStartMonth: number | null;
	readonly expirationTransitionTimeInMonths: number | null;
};

type ReviewSettings = {
	shouldReviewTrips: boolean;
	minAmountToReviewTrips: number | null;
	maxTripAgeInDaysForAutoApproval: number | null;
};

export type NewDivision = Pick<
	Division,
	| "name"
	| "status"
	| "isMainDivision"
	| "street"
	| "zip"
	| "city"
	| "country"
	| "phone"
	| "fax"
	| "stid"
	| "ustid"
	| "comment"
	| "providerSettings"
	| "isSachbezugFitnessEnabled"
>;

export type UpdatedDivision = Pick<
	Division,
	| "name"
	| "customerId"
	| "isMainDivision"
	| "street"
	| "zip"
	| "city"
	| "country"
	| "phone"
	| "fax"
	| "stid"
	| "ustid"
	| "excessBudgetSettings"
	| "autoLockDayOfMonth"
	| "pricingSettings"
	| "comment"
	| "reviewSettings"
	| "hasWebAppAccess"
	| "allowThirdPartyVouchers"
	| "providerSettings"
	| "isSachbezugFitnessEnabled"
	| "categories"
>;
